// Pagina "Documente diverse" - rescrisa in stilul wm (vezi workingmonth.css).
// Versiunea veche este pastrata in Misc.jsx.bak
import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Progress,
  Select,
  Table,
  Tooltip,
  Upload,
  message,
} from "antd";
import {
  InboxOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { API_URL, host } from "../../constants";
import "./workingmonth.css";

const { Dragger } = Upload;

const Badge = ({ tone = "neutral", children }) => (
  <span className={`wm-badge wm-badge-sm wm-badge-${tone}`}>{children}</span>
);

const PARSE_FILES = [
  "address", "apartament", "banca", "calculation", "calcultype", "customers",
  "indice", "object", "region", "serv_apart", "serv_furn", "service",
  "street", "luc",
];

const DOWNLOADS = [
  { key: "modificari", label: "Modificări", path: "modificari", file: "modificari.zip" },
  { key: "indici", label: "Indici", path: "indici", file: "indici.zip" },
  { key: "nume", label: "Nume", path: "modificarinume", file: "nume.zip" },
  { key: "luc", label: "LUC", path: "luc", file: "luc.zip" },
  { key: "contoare", label: "Modificări contoare", path: "contoare", file: "contoare.zip" },
  { key: "contoare_expired", label: "Expirare contoare", path: "contoare_expired", file: "contoare_expired.zip" },
  { key: "recalcule", label: "Recalcule", path: "recalcule", file: "recalcule.zip" },
];

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

// Istoricul unui fisier (ImportLog) se incarca paginat din backend
const HISTORY_PAGE = 20;

const fmtDate = (d) => (d ? moment(d).format("DD.MM.YYYY, HH:mm") : "—");
const fmtBytes = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};
const fmtDuration = (ms) => {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} s`;
  return `${Math.floor(sec / 60)} min ${sec % 60} s`;
};
const STATUS_BADGE = {
  ok: { tone: "good", text: "reușit" },
  error: { tone: "bad", text: "eroare" },
  running: { tone: "warn", text: "în lucru" },
};
const statusBadge = (status) => {
  const b = STATUS_BADGE[status] || { tone: "neutral", text: status };
  return <Badge tone={b.tone}>{b.text}</Badge>;
};
// eroarea ultimei parsari se refera la versiunea curenta a fisierului?
const errorOnCurrentFile = (r) =>
  r.lastParse?.status === "error" &&
  (!r.fileModified ||
    !r.lastParse.fileDate ||
    new Date(r.lastParse.fileDate).getTime() >=
      new Date(r.fileModified).getTime() - 1000);

const Misc = () => {
  const accessToken = useSelector(
    (state) => state.user.currentUser?.accessToken
  );
  const authHeaders = { headers: { Authorization: "Bearer " + accessToken } };

  // import DBF: o linie per fisier (fisier pe disc + ultima incarcare /
  // ultima parsare), progres live cat timp ruleaza o parsare
  const [summary, setSummary] = useState([]);
  const [history, setHistory] = useState({
    open: false,
    file: null,
    rows: [],
    total: 0,
    page: 1,
    loading: false,
  });
  const timerRef = useRef(null);
  const aliveRef = useRef(true);
  // descarcari
  const [ln, setLn] = useState(new Date().getMonth() + 1);
  const [an, setAn] = useState(new Date().getFullYear());
  const [downloading, setDownloading] = useState(null);
  // accese
  const [blocked, setBlocked] = useState(false);
  const [blockedClientIndici, setBlockedClientIndici] = useState(false);
  const [toggling, setToggling] = useState(false);
  // stergere plati curente
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /* ================= IMPORT DBF: SUMAR + PARSARE ================= */
  // sumarul se reincarca la 2 s cat timp ruleaza o parsare, altfel la 20 s
  const loadSummary = async () => {
    let parsing = false;
    try {
      const res = await axios.get(
        API_URL + "user/gestionar/import/summary",
        authHeaders
      );
      if (!aliveRef.current) return;
      setSummary(res.data.files || []);
      parsing = !!res.data.anyParsing;
    } catch (error) {
      console.log(error);
    }
    if (!aliveRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(loadSummary, parsing ? 2000 : 20000);
  };

  const refreshSoon = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(loadSummary, 800);
  };

  useEffect(() => {
    aliveRef.current = true;
    loadSummary();
    return () => {
      aliveRef.current = false;
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsingNow = summary.filter((r) => r.parsing).map((r) => r.file);

  const handleParsing = (name) => {
    // cererea ramane deschisa pana se termina parsarea; progresul vine din sumar
    setSummary((rows) =>
      rows.map((r) => (r.file === name ? { ...r, parsing: true, progress: 0 } : r))
    );
    message.success(`Parsarea „${name}.dbf” a pornit.`);
    axios
      .get(API_URL + "user/gestionar/startParse/" + name, authHeaders)
      .then((res) => {
        if (res.data.success) {
          message.success(
            `${name}.dbf — ${res.data.processed} înregistrări parsate.`
          );
        } else {
          message.error(
            `${name}.dbf — ${res.data.error || res.data.message || "parsarea a eșuat."}`
          );
        }
      })
      .catch((error) => {
        console.log(error);
        // fara raspuns HTTP (timeout de retea) parsarea continua pe server;
        // starea reala se vede in tabel
        if (error.response) {
          message.error(
            error.response.data?.message ||
              `Nu am putut porni parsarea „${name}.dbf”.`
          );
        }
      })
      .finally(refreshSoon);
    refreshSoon();
  };

  const loadHistory = async (file, page = 1) => {
    setHistory((h) => ({ ...h, open: true, file, page, loading: true }));
    try {
      const res = await axios.get(
        API_URL + "user/gestionar/import/history/" + file,
        {
          ...authHeaders,
          params: { take: HISTORY_PAGE, skip: (page - 1) * HISTORY_PAGE },
        }
      );
      setHistory((h) => ({
        ...h,
        rows: res.data.rows || [],
        total: res.data.total || 0,
        loading: false,
      }));
    } catch (error) {
      console.log(error);
      message.error("Nu am putut încărca istoricul fișierului.");
      setHistory((h) => ({ ...h, loading: false }));
    }
  };

  /* ================= UPLOAD ================= */
  const handleUpload = ({ file }) => {
    if (file.status === "done") {
      message.success(`${file.name} încărcat cu succes`);
      refreshSoon();
    } else if (file.status === "error") {
      const reason =
        file.response?.message || file.response?.error || file.error?.message;
      message.error(
        `${file.name} — încărcarea a eșuat.` + (reason ? ` ${reason}` : "")
      );
    }
  };

  /* ================= DESCARCARI ================= */
  const handleDownload = async (item) => {
    setDownloading(item.key);
    try {
      const res = await axios.get(
        API_URL + `user/gestionar/download/${item.path}/${ln}/${an}`,
        { ...authHeaders, responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", item.file);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(`${item.label} — descărcat pentru ${MONTHS_RO[ln - 1]} ${an}.`);
    } catch (error) {
      console.log(error);
      message.error(`${item.label} — descărcarea a eșuat.`);
    } finally {
      setDownloading(null);
    }
  };

  /* ================= ACCESE ================= */
  const getBlockStatus = async () => {
    try {
      const res = await axios.get(API_URL + "gestionar/modificari/blockageStatus");
      setBlocked(!!res.data.blocked);
    } catch (error) {
      console.log(error);
    }
  };
  const getBlockStatusClientIndici = async () => {
    try {
      const res = await axios.get(API_URL + "user/gestionar/client/blockageStatus");
      setBlockedClientIndici(!!res.data.blocked);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getBlockStatus();
    getBlockStatusClientIndici();
  }, []);

  const toggleModificari = async () => {
    setToggling(true);
    try {
      const route = blocked
        ? "user/gestionar/modificari/removeBlockage"
        : "user/gestionar/modificari/setBlockage";
      await axios.get(API_URL + route);
      message.success(
        blocked
          ? "Modificările au fost DEBLOCATE — gestionarii pot lucra."
          : "Modificările au fost BLOCATE."
      );
      await getBlockStatus();
    } catch (error) {
      message.error(error.message);
    } finally {
      setToggling(false);
    }
  };

  const toggleClientIndici = async () => {
    setToggling(true);
    try {
      const route = blockedClientIndici
        ? "user/gestionar/client/removeBlockage"
        : "user/gestionar/client/setBlockage";
      await axios.get(API_URL + route);
      message.success(
        blockedClientIndici
          ? "Prezentarea indicilor a fost PERMISĂ clienților."
          : "Prezentarea indicilor a fost BLOCATĂ."
      );
      await getBlockStatusClientIndici();
    } catch (error) {
      message.error(error.message);
    } finally {
      setToggling(false);
    }
  };

  /* ================= STERGERE PLATI CURENTE ================= */
  const deleteCurrentPlati = async () => {
    setDeleting(true);
    try {
      await axios.get(API_URL + "user/gestionar/reset/CurrentPayed", authHeaders);
      message.success("Plățile curente (Banca) au fost șterse.");
    } catch (error) {
      console.log(error);
      message.error(error.message);
    } finally {
      setDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  /* ================= COLOANE TABELE ================= */
  const importColumns = [
    {
      title: "Fișier",
      dataIndex: "file",
      key: "file",
      width: 170,
      render: (name, r) => (
        <div className="wm-user-cell">
          <div className="wm-user-name">{name}.dbf</div>
          <div className="wm-user-email">
            {r.fileExists
              ? `${fmtBytes(r.fileSize)} · ${fmtDate(r.fileModified)}`
              : "fișier lipsă pe server"}
          </div>
        </div>
      ),
    },
    {
      title: "Ultima încărcare",
      key: "upload",
      width: 165,
      render: (_, r) =>
        r.lastUpload ? (
          <div className="wm-user-cell">
            <span className="wm-log-time">{fmtDate(r.lastUpload.at)}</span>
            <div className="wm-user-email">{r.lastUpload.user || "—"}</div>
          </div>
        ) : (
          <span style={{ color: "#8b93a1" }}>—</span>
        ),
    },
    {
      title: "Ultima parsare",
      key: "parse",
      width: 250,
      render: (_, r) => {
        if (r.parsing) {
          return (
            <Progress
              percent={Math.round(r.progress || 0)}
              size="small"
              style={{ margin: 0, width: 200 }}
              strokeColor={(r.progress || 0) > 50 ? "#22c55e" : "#f59e0b"}
            />
          );
        }
        const p = r.lastParse;
        if (!p) return <span style={{ color: "#8b93a1" }}>—</span>;
        const info = [p.user || "—"];
        if (p.records != null) info.push(`${p.processed ?? 0}/${p.records} înreg.`);
        if (p.durationMs != null) info.push(fmtDuration(p.durationMs));
        return (
          <div className="wm-user-cell">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span className="wm-log-time">{fmtDate(p.at)}</span>
              {statusBadge(p.status)}
            </span>
            <div className="wm-user-email">{info.join(" · ")}</div>
          </div>
        );
      },
    },
    {
      title: "Stare",
      key: "state",
      width: 120,
      render: (_, r) => {
        if (r.parsing) return <Badge tone="warn">în lucru</Badge>;
        if (!r.fileExists) return <Badge tone="neutral">fără fișier</Badge>;
        if (errorOnCurrentFile(r)) {
          return (
            <Tooltip title={r.lastParse.message}>
              <span>
                <Badge tone="bad">eroare</Badge>
              </span>
            </Tooltip>
          );
        }
        if (r.needsParse) {
          return (
            <Tooltip title="Fișierul de pe server este mai nou decât ultima parsare reușită">
              <span>
                <Badge tone="warn">de parsat</Badge>
              </span>
            </Tooltip>
          );
        }
        return <Badge tone="good">la zi</Badge>;
      },
    },
    {
      title: "",
      key: "actions",
      width: 150,
      align: "right",
      render: (_, r) => (
        <span style={{ display: "inline-flex", gap: 6 }}>
          <Tooltip
            title={
              r.parsing
                ? `Se parsează ${r.file}.dbf...`
                : parsingNow.length
                  ? "Așteptați să se termine parsarea curentă"
                  : !r.fileExists
                    ? "Încărcați mai întâi fișierul"
                    : `Parsează ${r.file}.dbf`
            }
          >
            <button
              type="button"
              className={`wm-btn wm-btn-sm ${r.parsing ? "wm-btn-secondary wm-btn-loading" : "wm-btn-primary"}`}
              disabled={parsingNow.length > 0 || !r.fileExists}
              onClick={() => handleParsing(r.file)}
            >
              {r.parsing ? <span className="wm-spin" /> : <PlayCircleOutlined />}
            </button>
          </Tooltip>
          <button
            type="button"
            className="wm-btn wm-btn-sm wm-btn-secondary"
            onClick={() => loadHistory(r.file, 1)}
          >
            <HistoryOutlined /> Detalii
          </button>
        </span>
      ),
    },
  ];

  const historyColumns = [
    {
      title: "Data",
      dataIndex: "at",
      key: "at",
      width: 150,
      render: (v) => <span className="wm-log-time">{fmtDate(v)}</span>,
    },
    {
      title: "Acțiune",
      dataIndex: "action",
      key: "action",
      width: 110,
      render: (a) =>
        a === "upload" ? (
          <Badge tone="neutral">încărcare</Badge>
        ) : (
          <Badge tone="accent">parsare</Badge>
        ),
    },
    {
      title: "Rezultat",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: statusBadge,
    },
    {
      title: "Utilizator",
      dataIndex: "user",
      key: "user",
      width: 150,
      render: (u) => <span className="wm-log-email">{u || "—"}</span>,
    },
    {
      title: "Înregistrări",
      key: "records",
      width: 110,
      render: (_, r) =>
        r.records != null ? (
          <span className="wm-log-time">
            {r.processed ?? 0} / {r.records}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "Durată",
      dataIndex: "durationMs",
      key: "duration",
      width: 90,
      render: (v) => <span className="wm-log-time">{fmtDuration(v)}</span>,
    },
    {
      title: "Fișier",
      key: "file",
      width: 175,
      render: (_, r) =>
        r.fileSize != null ? (
          <span className="wm-log-email">
            {fmtBytes(r.fileSize)} · {fmtDate(r.fileDate)}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "Mesaj",
      dataIndex: "message",
      key: "message",
      ellipsis: { showTitle: false },
      render: (m) =>
        m ? (
          <Tooltip title={m}>
            <span style={{ color: "#b42318" }}>{m}</span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
  ];

  const historyExpanded = (r) => (
    <div style={{ fontSize: 12.5, display: "grid", gap: 6 }}>
      <div>
        <strong>Mesaj complet:</strong> {r.message || "—"}
      </div>
      {r.detail ? (
        <div>
          <strong>Înregistrarea care a oprit parsarea:</strong>
          <pre
            style={{
              margin: "4px 0 0",
              maxHeight: 280,
              overflow: "auto",
              fontSize: 12,
              background: "#f7f7f8",
              padding: 10,
              borderRadius: 8,
            }}
          >
            {JSON.stringify(r.detail, null, 2)}
          </pre>
        </div>
      ) : null}
    </div>
  );

  /* ================= RENDER ================= */
  return (
    <div className="wm-page">
      {/* ===== STARE ACCES ===== */}
      <div className="wm-stats" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className={`wm-stat ${blocked ? "wm-stat-bad" : "wm-stat-good"}`}>
          <div className="wm-stat-label">Modificări · gestionari</div>
          <div className="wm-stat-value" style={{ fontSize: 20 }}>
            {blocked ? "Blocat" : "Activ"}
          </div>
          <div className="wm-stat-meta" style={{ marginTop: 6 }}>
            <button
              type="button"
              className={`wm-btn wm-btn-sm ${blocked ? "wm-btn-success" : "wm-btn-danger"}${toggling ? " wm-btn-loading" : ""}`}
              onClick={toggleModificari}
            >
              {blocked ? <UnlockOutlined /> : <LockOutlined />}
              {blocked ? "Deblochează" : "Blochează"}
            </button>
          </div>
        </div>
        <div
          className={`wm-stat ${blockedClientIndici ? "wm-stat-bad" : "wm-stat-good"}`}
        >
          <div className="wm-stat-label">Prezentare indici · clienți</div>
          <div className="wm-stat-value" style={{ fontSize: 20 }}>
            {blockedClientIndici ? "Blocat" : "Activ"}
          </div>
          <div className="wm-stat-meta" style={{ marginTop: 6 }}>
            <button
              type="button"
              className={`wm-btn wm-btn-sm ${blockedClientIndici ? "wm-btn-success" : "wm-btn-danger"}${toggling ? " wm-btn-loading" : ""}`}
              onClick={toggleClientIndici}
            >
              {blockedClientIndici ? <UnlockOutlined /> : <LockOutlined />}
              {blockedClientIndici ? "Permite" : "Blochează"}
            </button>
          </div>
        </div>
        <div className={`wm-stat ${parsingNow.length ? "wm-stat-warn" : ""}`}>
          <div className="wm-stat-label">Parsare în lucru</div>
          <div className="wm-stat-value" style={{ fontSize: 20 }}>
            {parsingNow.length
              ? parsingNow
                  .map((f) => {
                    const r = summary.find((x) => x.file === f);
                    return `${f} · ${Math.round(r?.progress || 0)}%`;
                  })
                  .join(", ")
              : "—"}
          </div>
          <div className="wm-stat-meta">
            {parsingNow.length
              ? "nu porniți alte parsări până se termină"
              : "niciun fișier în procesare"}
          </div>
        </div>
      </div>

      {/* ===== 1. UPLOAD ===== */}
      <div className="wm-card" style={{ marginBottom: 14 }}>
        <h3 className="wm-card-title">Pasul 1 — Încărcați fișierele DBF</h3>
        <p className="wm-tab-desc">
          Trageți aici fișierele exportate din programul de calcul. Sunt
          acceptate doar: {PARSE_FILES.map((f) => `${f}.dbf`).join(", ")}.
        </p>
        <Dragger
          multiple
          onChange={handleUpload}
          action={host + "/import/upload"}
          headers={{ Authorization: "Bearer " + accessToken }}
          name="files"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Faceți clic sau trageți fișierele DBF în această zonă
          </p>
          <p className="ant-upload-hint">
            Se pot încărca mai multe fișiere odată. Fișierele cu alt nume sunt
            respinse automat.
          </p>
        </Dragger>
      </div>

      {/* ===== 2. PARSARE ===== */}
      <div className="wm-card" style={{ marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <h3 className="wm-card-title">Pasul 2 — Parsați fișierele încărcate</h3>
            <p className="wm-tab-desc">
              Fiecare rând este un fișier DBF acceptat: ultima încărcare și
              ultima parsare (cine, când, cu ce rezultat). „De parsat” înseamnă
              că fișierul de pe server este mai nou decât ultima parsare
              reușită. „Detalii” deschide istoricul complet al fișierului.
            </p>
          </div>
          <button
            type="button"
            className="wm-btn wm-btn-secondary wm-btn-sm"
            onClick={loadSummary}
          >
            <ReloadOutlined /> Reîncarcă
          </button>
        </div>
        <div className="wm-history">
          <Table
            rowKey="file"
            size="small"
            columns={importColumns}
            dataSource={summary}
            pagination={false}
            scroll={{ x: 860 }}
            locale={{ emptyText: "Se încarcă starea fișierelor..." }}
          />
        </div>
      </div>

      {/* ===== 3. DESCARCARI ===== */}
      <div className="wm-card" style={{ marginBottom: 14 }}>
        <h3 className="wm-card-title">
          Pasul 3 — Descărcați datele pentru calcul
        </h3>
        <div className="wm-filters" style={{ marginBottom: 12 }}>
          <div className="wm-filter">
            <span className="wm-filter-label">Luna</span>
            <Select
              value={ln}
              onChange={setLn}
              style={{ width: 150 }}
              options={MONTHS_RO.map((m, i) => ({ value: i + 1, label: `${i + 1} — ${m}` }))}
            />
          </div>
          <div className="wm-filter">
            <span className="wm-filter-label">Anul</span>
            <Select
              value={an}
              onChange={setAn}
              style={{ width: 110 }}
              options={Array.from({ length: 2040 - 2024 + 1 }, (_, i) => ({
                value: 2024 + i,
                label: `${2024 + i}`,
              }))}
            />
          </div>
          <div className="wm-filter" style={{ justifyContent: "flex-end" }}>
            <span className="wm-filter-label">&nbsp;</span>
            <Badge tone="neutral">
              Se descarcă pentru: {MONTHS_RO[ln - 1]} {an}
            </Badge>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {DOWNLOADS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`wm-btn wm-btn-secondary wm-btn-sm${downloading === item.key ? " wm-btn-loading" : ""}`}
              disabled={parsingNow.length > 0 || (downloading && downloading !== item.key)}
              onClick={() => handleDownload(item)}
            >
              {downloading === item.key ? (
                <span className="wm-spin" />
              ) : (
                <DownloadOutlined />
              )}
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== ZONA PERICULOASA ===== */}
      <div
        className="wm-card"
        style={{ border: "1px solid #f8d4d0", background: "#fffbfb" }}
      >
        <h3 className="wm-card-title" style={{ color: "#b42318" }}>
          Zonă periculoasă
        </h3>
        <div className="wm-action" style={{ padding: 0 }}>
          <div className="wm-action-info">
            <div className="wm-action-title">
              Șterge plățile curente (Banca)
            </div>
            <div className="wm-action-desc">
              Șterge TOATE datele din compartimentul „Sold la zi” și setează
              coloana „achitat” la 0. Fișierul DBF responsabil de acest
              compartiment este <strong>banca.dbf</strong>. În data de 5 a
              fiecărei luni ștergerea are loc oricum automat.
            </div>
          </div>
          <span className="wm-action-btn">
            <button
              type="button"
              className="wm-btn wm-btn-danger"
              disabled={parsingNow.length > 0}
              onClick={() => setIsDeleteOpen(true)}
            >
              <DeleteOutlined /> Șterge plăți curente
            </button>
          </span>
        </div>
      </div>

      <Modal
        open={isDeleteOpen}
        onOk={deleteCurrentPlati}
        onCancel={() => setIsDeleteOpen(false)}
        okText="Da, șterge tot"
        cancelText="Renunță"
        okButtonProps={{ danger: true, loading: deleting }}
        title="Ștergeți toate plățile curente?"
      >
        Toate datele din „Sold la zi” vor fi șterse, iar coloana „achitat” va fi
        setată la 0. Operațiunea nu poate fi anulată.
      </Modal>

      <Modal
        open={history.open}
        onCancel={() => setHistory((h) => ({ ...h, open: false }))}
        footer={null}
        width={1040}
        title={history.file ? `Istoric ${history.file}.dbf` : "Istoric"}
      >
        {/* fereastra antd se randeaza in afara .wm-page: reaplicam variabilele wm */}
        <div className="wm-page" style={{ maxWidth: "none" }}>
        <p className="wm-tab-desc">
          Încărcările și parsările fișierului, cele mai recente primele.
          Rândurile cu eroare se pot extinde pentru a vedea înregistrarea care a
          oprit parsarea.
        </p>
        <div className="wm-history">
          <Table
            rowKey="id"
            size="small"
            loading={history.loading}
            columns={historyColumns}
            dataSource={history.rows}
            scroll={{ x: 940 }}
            expandable={{
              rowExpandable: (r) => !!r.detail || !!r.message,
              expandedRowRender: historyExpanded,
            }}
            locale={{ emptyText: "Niciun eveniment înregistrat pentru acest fișier" }}
            pagination={{
              current: history.page,
              pageSize: HISTORY_PAGE,
              total: history.total,
              showSizeChanger: false,
              hideOnSinglePage: true,
              showTotal: (t, r2) => `${r2[0]}–${r2[1]} din ${t} înregistrări`,
              onChange: (page) => loadHistory(history.file, page),
            }}
          />
        </div>
        </div>
      </Modal>
    </div>
  );
};

export default Misc;
